import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnInit,
    Renderer2,
    ViewEncapsulation,
    Input,
    ViewChild,
} from "@angular/core";
import { S25EditableAbstract } from "../s25.editable.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Util } from "../../../util/s25-util";
import { AttachmentService, AttachmentItem } from "../../../services/attachment.service";
import { S25File, FileUploadComponent } from "../../s25-file-upload/s25.file.upload.component";

@TypeManagerDecorator("s25-ng-editable-file")
@Component({
    selector: "s25-ng-editable-file",
    template: `
        @if (init) {
            <div class="editable-file-section">
                @if (model?.files) {
                    @for (file of model?.files; track $index) {
                        <s25-ng-display-file
                            [fileName]="file.itemName"
                            [objectId]="file?.objectId"
                            [objectType]="file?.objectTypeId"
                            [file]="file"
                            [isNewAddFile]="file?.isNewItem ?? false"
                            [fileData]="file?.data"
                            [hasRemoveButton]="canEdit"
                            (onRemove)="remove($event)"
                        ></s25-ng-display-file>
                    }
                }
                @if (canEdit) {
                    <s25-ng-file-upload [multiple]="true" [showFiles]="false" [onAdd]="handleFileUpload">
                    </s25-ng-file-upload>
                }
            </div>
        }
    `,
    styles: `
        .editable-file-section {
            display: block;
            text-align: left; /* Make inline content (like text or buttons) align left */
            align-items: flex-start; /* If in flex layout parent */
            justify-content: flex-start;
            padding-left: 0; /* Prevent unexpected indent */
            margin-left: 0;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EditableFileComponent extends S25EditableAbstract<string> implements OnInit {
    @ViewChild(FileUploadComponent) fileUploadComponent: FileUploadComponent;
    @Input() model?: {
        files: AttachmentItem[];
        itemTypeId: number;
        itemId: number;
        addAction: (file: any) => Promise<void>;
        removeAction: (file: any) => Promise<void>;
        showLinks?: boolean;
    };
    @Input() showNewAddedFile?: boolean = false;
    @Input() canEdit: boolean = true;
    @Input() excludeCustAttrFiles?: boolean = false;

    init = false;

    private uploadQueue: S25File[] = [];
    private isUploading = false;

    getType = () => "file";

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
        private renderer: Renderer2,
    ) {
        super(elementRef, cd, zone, renderer);
    }

    ngOnInit(): void {
        this.init = true;
        this.cd.detectChanges();
    }

    getUrl(item: any): string {
        return AttachmentService.getFileUrl(this.model.itemTypeId, this.model.itemId, item.itemName);
    }

    encodedName(itemName: string): string {
        return encodeURIComponent(itemName);
    }

    getExtension(itemName: string): string {
        const parts = itemName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    async remove(file: AttachmentItem): Promise<void> {
        await this.model.removeAction(file);
        this.removeFromList(file);
    }

    private removeFromList(file: AttachmentItem): void {
        S25Util.array.remove(this.model.files, this.model.files.indexOf(file));
        this.cd.detectChanges();
    }

    handleFileUpload = async (file: S25File): Promise<void> => {
        this.uploadQueue.push(file);
        await this.processQueue();
    };

    async processQueue(): Promise<void> {
        if (this.isUploading) return;
        this.isUploading = true;

        const duplicateFiles: string[] = [];
        let fileLimitReached = false;

        while (this.uploadQueue.length > 0) {
            const file = this.uploadQueue.shift()!;
            const existingFiles = await this.getFiles();

            const isDuplicate = existingFiles.some((f) => f.itemName === file.name);
            if (isDuplicate) {
                duplicateFiles.push(file.name);
                continue;
            }

            this.validated(file);
            const added = await this.add(file);

            if (!added) {
                fileLimitReached = true;
                break; // Stop processing further files
            }
        }

        // Alert all duplicates at once
        if (duplicateFiles.length > 0) {
            const fileList = duplicateFiles.join(", ");
            alert(`The following file(s) have already been uploaded and were skipped: ${fileList}`);
        }

        if (fileLimitReached) {
            alert("The maximum number of files (10) have already been uploaded for this object.");
        }

        this.isUploading = false;
    }

    async add(file: any): Promise<boolean> {
        file.itemName = file.name;

        try {
            if (this.model.files.length >= 10) {
                return false; // Indicate limit reached
            }

            await this.model.addAction(file);
            this.model.files = await this.getFiles();
            if (this.excludeCustAttrFiles)
                this.model.files = this.model.files.filter((f) => !f.hasOwnProperty("cust_atrb_id"));
            await S25Util.delay(120);

            if (this.showNewAddedFile) {
                this.model.files.push({ ...file, itemName: file.name, isNewItem: true });
            }

            this.cd.detectChanges();
            return true; // File added successfully
        } catch (err) {
            this.removeFromList(file);
            S25Util.showError(err);
            this.cd.detectChanges();
            return false; // Indicate failure
        }
    }

    validated(file: S25File): string | undefined {
        if (!file.name) {
            return "File name cannot be empty";
        }
        if (file.name.length > 112) {
            return "The file name is too long. File names can be no longer than 112 characters";
        }
        return;
    }

    async getFiles(): Promise<any[]> {
        if (this.model?.itemId) {
            const data = await AttachmentService.getFileList(this.model.itemTypeId, this.model.itemId);
            return data || [];
        } else {
            return this.model.files;
        }
    }
}
